/**
 * Subscribes to the instance-wide system events channel (`GET /api/events`) to
 * learn when threads are created, updated, or terminated. We use it to track
 * *subagents*: a subagent runs in its own child thread, so the only live signal
 * of "a subagent is working" is its child thread appearing here with our thread
 * as its `parent` (and disappearing/terminating when it finishes).
 */
import type { ApiClient } from "./api.ts";
import { Heartbeat } from "./heartbeat.ts";

/** The subset of a thread registry entry we care about. */
export interface ThreadEntry {
  id: string;
  agent_name?: string | null;
  tags?: string[] | null;
  parent?: string | null;
  terminated?: number | null;
  created_at?: number;
}

export interface SystemEventHooks {
  onThreadCreated(thread: ThreadEntry): void;
  onThreadUpdated(thread: ThreadEntry): void;
  onThreadDeleted(threadId: string): void;
  /** Fired whenever the socket (re)connects, so the consumer can re-snapshot
   *  state that live deltas alone can't reconstruct after a gap. */
  onOpen?(): void;
}

export class SystemEvents {
  private ws: WebSocket | null = null;
  private closed = false;
  private heartbeat: Heartbeat | null = null;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private api: ApiClient,
    private hooks: SystemEventHooks
  ) {}

  connect(): void {
    this.openSocket();
  }

  private openSocket(): void {
    if (this.closed) return;
    const url = `${this.api.wsEndpoint}/api/events?token=${encodeURIComponent(this.api.bearer)}`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.addEventListener("open", () => {
      this.reconnectAttempt = 0;
      this.startHeartbeat(ws);
      this.hooks.onOpen?.();
    });
    ws.addEventListener("message", (ev) => {
      if (this.ws === ws) this.heartbeat?.markAlive();
      this.onMessage(String((ev as MessageEvent).data));
    });
    ws.addEventListener("error", () => this.handleDrop(ws));
    ws.addEventListener("close", () => this.handleDrop(ws));
  }

  private startHeartbeat(ws: WebSocket): void {
    this.stopHeartbeat();
    this.heartbeat = new Heartbeat(ws, () => this.handleDrop(ws));
    this.heartbeat.start();
  }

  private stopHeartbeat(): void {
    if (this.heartbeat) {
      this.heartbeat.stop();
      this.heartbeat = null;
    }
  }

  private onMessage(raw: string): void {
    let msg: any;
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    if (msg?.type === "thread_created" && msg.thread) this.hooks.onThreadCreated(msg.thread);
    else if (msg?.type === "thread_updated" && msg.thread) this.hooks.onThreadUpdated(msg.thread);
    else if (msg?.type === "thread_deleted" && typeof msg.threadId === "string") {
      this.hooks.onThreadDeleted(msg.threadId);
    }
  }

  private handleDrop(ws: WebSocket): void {
    if (this.ws !== ws) return;
    this.ws = null;
    this.stopHeartbeat();
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.closed || this.reconnectTimer) return;
    this.reconnectAttempt++;
    const base = Math.min(500 * 2 ** (this.reconnectAttempt - 1), 15000);
    const delay = base + Math.floor(Math.random() * 400);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }

  close(): void {
    this.closed = true;
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
  }
}
